import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { GAMES } from "@/components/storefront/games";
import type { GameMeta } from "@/components/storefront/games/types";

export const Route = createFileRoute("/dev/games")({
  head: () => ({
    meta: [
      { title: "Δοκιμή παιχνιδιών — MrPanda.gr" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DevGamesPage,
});

type Result = "won" | "lost" | null;

function DevGamesPage() {
  const [active, setActive] = useState<GameMeta | null>(null);
  const [result, setResult] = useState<Result>(null);
  // `runId` forces a fresh mount of the game component on every (re)start so
  // its internal state resets cleanly.
  const [runId, setRunId] = useState(0);

  const start = (g: GameMeta) => {
    setActive(g);
    setResult(null);
    setRunId((n) => n + 1);
  };

  const restart = () => {
    setResult(null);
    setRunId((n) => n + 1);
  };

  return (
    <div className="mx-auto min-h-screen max-w-2xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="text-5xl">🐼</div>
        <h1 className="mt-2 text-2xl font-extrabold text-foreground">Δοκιμή παιχνιδιών</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Σελίδα δοκιμών (δεν εμφανίζεται στο μενού/Google). Διάλεξε παιχνίδι και παίξε το —
          χωρίς σύνδεση ή κουπόνι.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {GAMES.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => start(g)}
            className={`mp-bouncy rounded-2xl border p-4 text-left transition-colors ${
              active?.key === g.key
                ? "border-primary bg-primary/10"
                : "border-border bg-card hover:bg-accent"
            }`}
          >
            <div className="text-3xl">{g.emoji}</div>
            <div className="mt-2 font-bold text-foreground">{g.title}</div>
            <div className="mt-1 text-xs text-muted-foreground">{g.goal}</div>
          </button>
        ))}
      </div>

      {active && (
        <section className="mt-8 rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <div className="font-bold text-foreground">
                {active.emoji} {active.title}
              </div>
              <div className="text-xs text-muted-foreground">{active.hint}</div>
            </div>
            <button
              type="button"
              onClick={restart}
              className="mp-bouncy rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              🔄 Επανεκκίνηση
            </button>
          </div>

          {result ? (
            <div className="grid place-items-center gap-3 py-10 text-center">
              <div className="text-5xl">{result === "won" ? "🎉" : "😅"}</div>
              <div className="text-lg font-bold text-foreground">
                {result === "won" ? "Νίκησες!" : "Έχασες!"}
              </div>
              <div className="text-sm text-muted-foreground">
                Το callback {result === "won" ? "onWin" : "onLose"} κλήθηκε σωστά.
              </div>
              <button
                type="button"
                onClick={restart}
                className="mp-bouncy mt-2 rounded-full border border-border bg-background px-5 py-2 text-sm font-semibold text-foreground hover:bg-accent"
              >
                Παίξε ξανά
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <active.Component
                key={runId}
                onWin={() => setResult("won")}
                onLose={() => setResult("lost")}
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
